/**
 * FloatingLauncher — Customizable search-bar style launcher that mounts inside
 * the chat widget's shadow DOM.
 *
 * Follows the same pattern as `makePillLauncher`: the chat widget runtime
 * calls `createFloatingLauncher()` internally when `config.floatingLauncher`
 * is set — no host-side `apply()` step is needed.
 *
 * In the injector, just pass the option through chat config:
 *
 *   chat: {
 *     floatingLauncher: {
 *       primaryColor: '#ec6e00',
 *       placeholders: ['Ürün ara', 'Karşılaştır', 'Soru sor'],
 *     },
 *   }
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FloatingLauncherOptions {
  // ---- Visual ---------------------------------------------------------------

  /** Brand accent color — focus ring, wave bars, border animation. Default: '#6366f1' */
  primaryColor?: string;

  /** Input bar background. Default: '#ffffff' */
  backgroundColor?: string;

  /** Input text color. Default: '#111827' */
  textColor?: string;

  /** Placeholder text color. Default: '#9ca3af' */
  placeholderColor?: string;

  /** Icon / button color. Default: '#6b7280' */
  iconColor?: string;

  /** Normal-state border color. Default: '#e5e7eb' */
  borderColor?: string;

  /** Input bar corner radius. Default: '999px' */
  borderRadius?: string;

  /** Input bar height. Default: '56px' */
  height?: string;

  /**
   * Maximum width of the input bar.
   * Can also be set via CSS: `--gengage-floating-launcher-max-width`.
   * Default: '680px'
   */
  maxWidth?: string;

  /** CSS font-family string. Default: 'inherit' */
  fontFamily?: string;

  // ---- Behavior -------------------------------------------------------------

  /**
   * Placeholder texts that cycle in sequence.
   * Default: ['Ürün ara', 'Karşılaştır', 'Soru sor']
   */
  placeholders?: string[];

  /** Milliseconds between placeholder rotations. Default: 2000 */
  placeholderInterval?: number;

  /**
   * Web Speech API language code.
   * Default: 'tr-TR'
   */
  voiceLang?: string;

  /**
   * Animate a rainbow border ring while voice recording is active.
   * Default: true
   */
  showBorderAnimation?: boolean;

  // ---- Accessibility / i18n ------------------------------------------------

  sendAriaLabel?: string;
  micAriaLabel?: string;
  cancelAriaLabel?: string;
  openChatAriaLabel?: string;
}

export interface FloatingLauncherKit {
  /** Root container element — the runtime appends this to the shadow DOM. */
  readonly container: HTMLElement;

  /**
   * Show the "open chat" icon button. The runtime calls this after the user's
   * first message has been successfully sent.
   */
  showOpenChatButton(): void;

  /** Release all resources. Called by the widget's `destroy()`. */
  destroy(): void;
}

interface SpeechRecognitionAlternativeLike {
  transcript?: string;
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionResultListLike {
  length: number;
  [index: number]: SpeechRecognitionResultLike | undefined;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

// ---------------------------------------------------------------------------
// Styles injected into the shadow root
// ---------------------------------------------------------------------------

const STYLE_ID = 'gengage-floating-launcher-style';

function buildCSS(opts: {
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  placeholderColor: string;
  iconColor: string;
  borderColor: string;
  borderRadius: string;
  height: string;
  maxWidth: string;
  fontFamily: string;
}): string {
  return `
@property --gengage-fl-ring-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

@keyframes gengage-fl-ring-spin {
  to { --gengage-fl-ring-angle: 360deg; }
}

@keyframes gengage-fl-wave-bar {
  0%, 100% { height: 4px; }
  50%       { height: 12px; }
}

.gengage-fl {
  --gengage-fl-primary:     ${opts.primaryColor};
  --gengage-fl-bg:          ${opts.backgroundColor};
  --gengage-fl-text:        ${opts.textColor};
  --gengage-fl-placeholder: ${opts.placeholderColor};
  --gengage-fl-icon:        ${opts.iconColor};
  --gengage-fl-border:      ${opts.borderColor};
  --gengage-fl-radius:      ${opts.borderRadius};
  --gengage-fl-height:      ${opts.height};
  --gengage-fl-max-width:   var(--gengage-floating-launcher-max-width, ${opts.maxWidth});
  --gengage-fl-font:        ${opts.fontFamily};

  position: fixed;
  bottom: var(--gengage-floating-launcher-bottom, 24px);
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: var(--gengage-fl-max-width);
  padding: 0 var(--gengage-floating-launcher-h-padding, 16px);
  z-index: 999;
  box-sizing: border-box;
  pointer-events: auto;
  transition: opacity 0.2s ease, transform 0.2s ease;
  font-family: var(--gengage-fl-font);
}

.gengage-chat-root--open .gengage-fl {
  opacity: 0;
  pointer-events: none;
  transform: translateX(-50%) translateY(8px);
}

.gengage-chat-root--mobile .gengage-fl {
  bottom: calc(var(--gengage-floating-launcher-mobile-bottom, 16px) + env(safe-area-inset-bottom, 0px));
}

.gengage-fl-inner {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.gengage-fl-ring {
  position: absolute;
  inset: -2px;
  border-radius: calc(var(--gengage-fl-radius) + 2px);
  opacity: 0;
  pointer-events: none;
  z-index: 0;
  transition: opacity 0.3s ease;
}

.gengage-fl-ring.is-active {
  opacity: 1;
  background: conic-gradient(
    from var(--gengage-fl-ring-angle),
    #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3, #ff0000
  );
  animation: gengage-fl-ring-spin 4s linear infinite;
}

.gengage-fl-bar {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--gengage-fl-height);
  background: var(--gengage-fl-bg);
  border: 1.5px solid var(--gengage-fl-border);
  border-radius: var(--gengage-fl-radius);
  padding: 0 8px 0 18px;
  gap: 4px;
  box-sizing: border-box;
  transition: border-color 0.2s ease;
  overflow: hidden;
}

.gengage-fl-bar:focus-within {
  border-color: var(--gengage-fl-primary);
}

.gengage-fl-logo {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  color: var(--gengage-fl-icon);
  margin-right: 6px;
}

.gengage-fl-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 15px;
  color: var(--gengage-fl-text);
  caret-color: var(--gengage-fl-primary);
}

.gengage-fl-input::placeholder {
  color: var(--gengage-fl-placeholder);
}

.gengage-fl-input::-webkit-search-cancel-button,
.gengage-fl-input::-webkit-search-decoration {
  -webkit-appearance: none;
  display: none;
}

.gengage-fl-transcript {
  flex: 1 1 auto;
  min-width: 0;
  display: none;
  align-items: center;
  gap: 2px;
  font-size: 15px;
  color: var(--gengage-fl-text);
  overflow: hidden;
  white-space: nowrap;
}

.gengage-fl-transcript-interim {
  color: var(--gengage-fl-placeholder);
}

.gengage-fl-btn {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: var(--gengage-fl-icon);
  padding: 0;
  transition: background 0.15s ease, color 0.15s ease;
}

.gengage-fl-btn:hover {
  background: color-mix(in srgb, var(--gengage-fl-primary) 10%, transparent);
  color: var(--gengage-fl-primary);
}

.gengage-fl-btn-send {
  color: var(--gengage-fl-primary);
}

.gengage-fl-btn-mic.is-recording {
  color: var(--gengage-fl-primary);
}

.gengage-fl-wave {
  display: none;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  height: 14px;
  width: 20px;
}

.gengage-fl-btn-mic.is-recording .gengage-fl-wave    { display: flex; }
.gengage-fl-btn-mic.is-recording .gengage-fl-mic-svg { display: none; }

.gengage-fl-wave span {
  display: block;
  width: 3px;
  height: 4px;
  background: var(--gengage-fl-primary);
  border-radius: 2px;
  transition: height 0.05s linear;
}

.gengage-fl-wave.is-css-animated span {
  animation: gengage-fl-wave-bar 600ms ease-in-out infinite;
}
.gengage-fl-wave.is-css-animated span:nth-child(1) { animation-delay: 0ms; }
.gengage-fl-wave.is-css-animated span:nth-child(2) { animation-delay: 120ms; }
.gengage-fl-wave.is-css-animated span:nth-child(3) { animation-delay: 240ms; }
.gengage-fl-wave.is-css-animated span:nth-child(4) { animation-delay: 360ms; }

.gengage-fl-cancel {
  flex: 0 0 auto;
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: var(--gengage-fl-icon);
  padding: 0;
  margin-left: 4px;
  transition: background 0.15s ease, color 0.15s ease;
}

.gengage-fl-cancel:hover {
  background: color-mix(in srgb, #ef4444 10%, transparent);
  color: #ef4444;
}
`.trim();
}

// ---------------------------------------------------------------------------
// HTML
// ---------------------------------------------------------------------------

function buildHTML(): string {
  return `
<div class="gengage-fl-inner">
  <div class="gengage-fl-ring" aria-hidden="true"></div>
  <div class="gengage-fl-bar">
    <span class="gengage-fl-logo" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
           fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="m21 21-4.34-4.34"/>
        <circle cx="11" cy="11" r="8"/>
      </svg>
    </span>
    <input type="search" class="gengage-fl-input" name="search"
           autocomplete="off" />
    <div class="gengage-fl-transcript" aria-live="polite">
      <span class="gengage-fl-transcript-final"></span>
      <span class="gengage-fl-transcript-interim"></span>
    </div>
    <button type="button" class="gengage-fl-btn gengage-fl-btn-send"
            style="display:none">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
           fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"/>
        <path d="m21.854 2.147-10.94 10.939"/>
      </svg>
    </button>
    <button type="button" class="gengage-fl-btn gengage-fl-btn-mic"
            aria-label="">
      <div class="gengage-fl-wave" aria-hidden="true">
        <span></span><span></span><span></span><span></span>
      </div>
      <svg class="gengage-fl-mic-svg" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
           viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3Z"/>
        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
        <line x1="12" x2="12" y1="19" y2="23"/>
        <line x1="8" x2="16" y1="23" y2="23"/>
      </svg>
    </button>
    <button type="button" class="gengage-fl-btn gengage-fl-btn-open-chat"
            style="display:none">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
           fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    </button>
  </div>
  <button type="button" class="gengage-fl-cancel"
          style="display:none">
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  </button>
</div>`.trim();
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createFloatingLauncher(
  options: FloatingLauncherOptions,
  callbacks: { onSend: (text: string) => void; onOpenChat: () => void },
  shadowRoot: ShadowRoot,
): FloatingLauncherKit {
  const {
    primaryColor = '#6366f1',
    backgroundColor = '#ffffff',
    textColor = '#111827',
    placeholderColor = '#9ca3af',
    iconColor = '#6b7280',
    borderColor = '#e5e7eb',
    borderRadius = '999px',
    height = '56px',
    maxWidth = '680px',
    fontFamily = 'inherit',
    placeholders = ['Ürün ara', 'Karşılaştır', 'Soru sor'],
    placeholderInterval = 2000,
    voiceLang = 'tr-TR',
    showBorderAnimation = true,
    sendAriaLabel = 'Gönder',
    micAriaLabel = 'Sesli giriş',
    cancelAriaLabel = 'Ses kaydını durdur',
    openChatAriaLabel = "Chat'i aç",
  } = options;

  // ---- Inject shared CSS into shadow root ----------------------------------

  if (!shadowRoot.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = buildCSS({
      primaryColor,
      backgroundColor,
      textColor,
      placeholderColor,
      iconColor,
      borderColor,
      borderRadius,
      height,
      maxWidth,
      fontFamily,
    });
    shadowRoot.appendChild(style);
  }

  // ---- DOM -----------------------------------------------------------------

  const container = document.createElement('div');
  container.className = 'gengage-fl';
  container.dataset['gengagePart'] = 'floating-launcher';
  container.innerHTML = buildHTML();

  const inputEl = container.querySelector<HTMLInputElement>('.gengage-fl-input')!;
  const transcriptEl = container.querySelector<HTMLDivElement>('.gengage-fl-transcript')!;
  const transcriptFinalEl = container.querySelector<HTMLSpanElement>('.gengage-fl-transcript-final')!;
  const transcriptInterimEl = container.querySelector<HTMLSpanElement>('.gengage-fl-transcript-interim')!;
  const sendBtn = container.querySelector<HTMLButtonElement>('.gengage-fl-btn-send')!;
  const micBtn = container.querySelector<HTMLButtonElement>('.gengage-fl-btn-mic')!;
  const openChatBtn = container.querySelector<HTMLButtonElement>('.gengage-fl-btn-open-chat')!;
  const cancelBtn = container.querySelector<HTMLButtonElement>('.gengage-fl-cancel')!;
  const waveEl = container.querySelector<HTMLDivElement>('.gengage-fl-wave')!;
  const ringEl = container.querySelector<HTMLDivElement>('.gengage-fl-ring')!;

  inputEl.placeholder = placeholders[0] ?? '';
  sendBtn.setAttribute('aria-label', sendAriaLabel);
  micBtn.setAttribute('aria-label', micAriaLabel);
  cancelBtn.setAttribute('aria-label', cancelAriaLabel);
  openChatBtn.setAttribute('aria-label', openChatAriaLabel);

  // ---- State ---------------------------------------------------------------

  let isRecording = false;
  let pendingVoiceFinalize = false;
  let voiceTranscript = '';
  let hasSentFirstMessage = false;
  let speechRecognition: SpeechRecognitionLike | null = null;

  let placeholderIndex = 0;
  let placeholderTimer: ReturnType<typeof setTimeout> | null = null;

  let mediaStream: MediaStream | null = null;
  let audioCtx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let frameId: number | null = null;
  let audioMeterRunId = 0;

  // ---- Visibility ----------------------------------------------------------

  function syncVisibility(): void {
    const hasText = inputEl.value.trim().length > 0;
    micBtn.classList.toggle('is-recording', isRecording);

    if (isRecording) {
      sendBtn.style.display = 'flex';
      micBtn.style.display = 'flex';
      cancelBtn.style.display = 'flex';
      openChatBtn.style.display = 'none';
      return;
    }

    sendBtn.style.display = hasText ? 'flex' : 'none';
    micBtn.style.display = hasText ? 'none' : 'flex';
    cancelBtn.style.display = 'none';
    openChatBtn.style.display = !hasText && hasSentFirstMessage ? 'flex' : 'none';
  }

  // ---- Placeholder rotation ------------------------------------------------

  function rotatePlaceholder(): void {
    placeholderIndex = (placeholderIndex + 1) % placeholders.length;
    inputEl.placeholder = placeholders[placeholderIndex] ?? '';
    placeholderTimer = setTimeout(rotatePlaceholder, placeholderInterval);
  }

  function startPlaceholders(): void {
    if (placeholderTimer !== null || placeholders.length <= 1) return;
    placeholderTimer = setTimeout(rotatePlaceholder, placeholderInterval);
  }

  function stopPlaceholders(): void {
    if (placeholderTimer !== null) {
      clearTimeout(placeholderTimer);
      placeholderTimer = null;
    }
  }

  // ---- Voice UI reset ------------------------------------------------------

  /**
   * Shared teardown for the recording UI. Used by trySend, the cancel handler
   * and finalizeVoice so the three sites stay in sync.
   *
   * `keepTranscript` controls whether the captured transcript is committed to
   * the input before the recording state is cleared:
   *   - `true`  → final transcript lands in the input (finalizeVoice).
   *   - `false` → transcript is discarded (trySend / cancel).
   * `clearInput` additionally empties the input field — used by cancel (a true
   * discard) but NOT by trySend, which reads inputEl.value right after to send it.
   */
  function resetVoiceUI(opts?: { keepTranscript?: boolean; clearInput?: boolean }): void {
    const keepTranscript = opts?.keepTranscript ?? false;
    pendingVoiceFinalize = false;
    isRecording = false;
    stopAudioMeter();
    if (showBorderAnimation) stopRing();
    if (keepTranscript) {
      if (voiceTranscript.trim()) inputEl.value = voiceTranscript.trim();
    } else {
      voiceTranscript = '';
      if (opts?.clearInput) inputEl.value = '';
    }
    inputEl.style.display = '';
    transcriptEl.style.display = 'none';
    transcriptFinalEl.textContent = '';
    transcriptInterimEl.textContent = '';
  }

  // ---- Send ----------------------------------------------------------------

  function trySend(): void {
    if (isRecording) {
      stopVoice();
      resetVoiceUI({ keepTranscript: false });
    }

    const text = inputEl.value.trim();
    if (text) {
      callbacks.onSend(text);
      if (!hasSentFirstMessage) hasSentFirstMessage = true;
    }
    inputEl.value = '';
    voiceTranscript = '';
    syncVisibility();
  }

  // ---- Voice ---------------------------------------------------------------

  function startVoice(): void {
    const speechWindow = window as Window & {
      SpeechRecognition?: SpeechRecognitionCtor;
      webkitSpeechRecognition?: SpeechRecognitionCtor;
    };
    const SR = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!SR) {
      console.warn('[FloatingLauncher] SpeechRecognition not supported.');
      return;
    }

    if (!speechRecognition) {
      speechRecognition = new SR();
      speechRecognition.continuous = true;
      speechRecognition.interimResults = true;
      speechRecognition.lang = voiceLang;
    }

    isRecording = true;
    pendingVoiceFinalize = true;
    voiceTranscript = '';
    inputEl.value = '';
    inputEl.style.display = 'none';
    transcriptEl.style.display = 'flex';
    transcriptFinalEl.textContent = '';
    transcriptInterimEl.textContent = '';
    syncVisibility();
    void startAudioMeter();
    if (showBorderAnimation) startRing();

    speechRecognition.onresult = (event: SpeechRecognitionEventLike): void => {
      if (!isRecording) return;
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (!r) continue;
        const t: string = r[0]?.transcript ?? '';
        if (r.isFinal) final += t;
        else interim += t;
      }
      voiceTranscript += final;
      transcriptFinalEl.textContent = voiceTranscript;
      transcriptInterimEl.textContent = interim;
      inputEl.value = `${voiceTranscript} ${interim}`.trim();
      syncVisibility();
    };

    speechRecognition.onend = (): void => finalizeVoice();
    speechRecognition.onerror = (): void => finalizeVoice();

    try {
      speechRecognition.start();
    } catch {
      finalizeVoice();
    }
  }

  function stopVoice(): void {
    try {
      speechRecognition?.stop();
    } catch {
      /* ignore */
    }
  }

  function finalizeVoice(): void {
    if (!pendingVoiceFinalize) return;
    resetVoiceUI({ keepTranscript: true });
    syncVisibility();
  }

  // ---- Audio meter ---------------------------------------------------------

  async function startAudioMeter(): Promise<void> {
    const runId = ++audioMeterRunId;
    const bars = Array.from(waveEl.querySelectorAll('span')) as HTMLElement[];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!isRecording || runId !== audioMeterRunId) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      mediaStream = stream;
      const audioWindow = window as Window & {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      };
      const AudioContextCtor = audioWindow.AudioContext ?? audioWindow.webkitAudioContext;
      if (!AudioContextCtor) {
        mediaStream = null;
        stream.getTracks().forEach((track) => track.stop());
        throw new Error('AudioContext not supported');
      }
      const createdAudioCtx = new AudioContextCtor();
      if (!isRecording || runId !== audioMeterRunId) {
        mediaStream = null;
        stream.getTracks().forEach((track) => track.stop());
        void createdAudioCtx.close().catch((): void => undefined);
        return;
      }
      audioCtx = createdAudioCtx;
      analyser = createdAudioCtx.createAnalyser();
      analyser.fftSize = 256;
      const src = createdAudioCtx.createMediaStreamSource(stream);
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = (): void => {
        if (!isRecording || runId !== audioMeterRunId || !analyser) return;
        analyser.getByteFrequencyData(data);
        const step = Math.max(1, Math.floor(data.length / bars.length));
        bars.forEach((b, i) => {
          b.style.height = `${4 + Math.round(((data[i * step] ?? 0) / 255) * 8)}px`;
        });
        frameId = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      if (!isRecording || runId !== audioMeterRunId) return;
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
      if (analyser) {
        analyser.disconnect();
        analyser = null;
      }
      if (audioCtx) {
        void audioCtx.close().catch((): void => undefined);
        audioCtx = null;
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
        mediaStream = null;
      }
      waveEl.classList.add('is-css-animated');
    }
  }

  function stopAudioMeter(): void {
    audioMeterRunId += 1;
    waveEl.classList.remove('is-css-animated');
    if (frameId !== null) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }
    if (analyser) {
      analyser.disconnect();
      analyser = null;
    }
    if (audioCtx) {
      audioCtx.close().catch((): void => undefined);
      audioCtx = null;
    }
    if (mediaStream) {
      mediaStream.getTracks().forEach((t) => t.stop());
      mediaStream = null;
    }
    (Array.from(waveEl.querySelectorAll('span')) as HTMLElement[]).forEach((b) => {
      b.style.height = '4px';
    });
  }

  // ---- Border ring ---------------------------------------------------------

  function startRing(): void {
    ringEl.classList.add('is-active');
  }
  function stopRing(): void {
    ringEl.classList.remove('is-active');
  }

  // ---- Event wiring --------------------------------------------------------

  sendBtn.addEventListener('click', trySend);

  micBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRecording) stopVoice();
    else startVoice();
  });

  cancelBtn.addEventListener('click', () => {
    if (!isRecording) return;
    stopVoice();
    resetVoiceUI({ keepTranscript: false, clearInput: true });
    syncVisibility();
  });

  openChatBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    callbacks.onOpenChat();
  });

  inputEl.addEventListener('input', syncVisibility);
  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') trySend();
  });

  syncVisibility();
  startPlaceholders();

  // ---- Public API ----------------------------------------------------------

  function showOpenChatButton(): void {
    hasSentFirstMessage = true;
    syncVisibility();
  }

  function destroy(): void {
    stopPlaceholders();
    stopAudioMeter();
    stopRing();
    if (isRecording) {
      isRecording = false;
      stopVoice();
    }
    if (speechRecognition) {
      speechRecognition.onresult = null;
      speechRecognition.onend = null;
      speechRecognition.onerror = null;
      speechRecognition = null;
    }
  }

  return { container, showOpenChatButton, destroy };
}
